import { HourlyWeather, WeatherInfo } from '../types';

/**
 * Determina la condición del clima basado en el código del tiempo (WMO Weather Code)
 * 
 * @param code - Código del tiempo según WMO (World Meteorological Organization)
 * @returns Condición del clima: 'sunny', 'cloudy' o 'rainy'
 * 
 * @example
 * ```typescript
 * const condition = getWeatherCondition(0); // 'sunny'
 * const condition = getWeatherCondition(61); // 'rainy'
 * ```
 */
export const getWeatherCondition = (code: number): 'sunny' | 'cloudy' | 'rainy' => {
    if (code === 0 || code === 1) return 'sunny';
    if (code >= 2 && code <= 48) return 'cloudy';
    if (code >= 51) return 'rainy';
    return 'sunny'; 
};

/**
 * Formatea una fecha para mostrar en formato largo en español de Chile
 * 
 * @param dateStr - Fecha en formato YYYY-MM-DD
 * @returns Fecha formateada (ej: "jueves 15 de enero")
 * 
 * @example
 * ```typescript
 * const formatted = getFormattedDate('2025-01-15'); // "jueves 15 de enero"
 * ```
 */
export const getFormattedDate = (dateStr: string): string => {
  if (!dateStr) return 'Selecciona una fecha';
  const date = new Date(dateStr + 'T00:00:00'); 
  return new Intl.DateTimeFormat('es-CL', { weekday: 'long', day: 'numeric', month: 'long' }).format(date);
};

/**
 * Obtiene el nombre corto del día de la semana en español
 * 
 * @param dateStr - Fecha en formato YYYY-MM-DD
 * @returns Nombre corto del día (ej: "jue" para jueves)
 * 
 * @example
 * ```typescript
 * const dayName = getDayName('2025-01-15'); // "jue"
 * ```
 */
export const getDayName = (dateStr: string): string => {
  const date = new Date(dateStr + 'T00:00:00');
  return new Intl.DateTimeFormat('es-CL', { weekday: 'short' }).format(date).toLowerCase().replace('.', '');
};

/**
 * Extrae el número del día de una fecha en formato YYYY-MM-DD
 * 
 * @param dateStr - Fecha en formato YYYY-MM-DD
 * @returns Número del día como string (ej: "15")
 * 
 * @example
 * ```typescript
 * const dayNum = getDayNumber('2025-01-15'); // "15"
 * ```
 */
export const getDayNumber = (dateStr: string): string => {
    return dateStr.split('-')[2];
};

